import React from 'react';
import { SanityBlock, SanityFigure, SanityLink } from '../../../../graphql-types';

import { useLightbox } from '../molecules/lightbox';
import { SanityImage } from '../molecules/sanity-image';

import { Typography } from '../atoms/typography';
import { RichText } from '../molecules/rich-text';
import { Container } from '../atoms/container';
import { SanityLinkList } from '../molecules/sanity-link-list';
import Button from '../atoms/button';
import clsx from 'clsx';
import translate from '../../../translations';
import { Language } from '../../../interfaces';

type TitleSize = 'default' | 'large';

type ImageGalleryBlockProps = {
    titleSize?: TitleSize;
    className?: string;
    layout?: '1' | '2' | '3';
    title: string;
    description?: SanityBlock[];
    images: SanityFigure[];
    links?: SanityLink[];
    language: Language;
};

export function ImageGalleryBlock({
    title,
    description,
    links,
    layout = '2',
    className,
    titleSize = 'default',
    language,
    images,
}: ImageGalleryBlockProps) {
    const lightbox = useLightbox({ images });

    const imageCount = images.length;
    const imageLayout = imageCount === 1 ? '1' : imageCount === 2 ? '2' : layout;

    const showAllButton = imageCount > 1;

    const visibleImages = images.filter((_, index) => {
        if (imageLayout === '1') {
            return index < 1;
        }
        if (imageLayout === '2') {
            return index < 2;
        }
        return index < 3;
    });

    const imageOne = visibleImages[0];
    const imageTwo = visibleImages[1];
    const imageThree = visibleImages[2];

    const imageOneContain = !!imageOne?.contain;
    const imageTwoContain = !!imageTwo?.contain;
    const imageThreeContain = !!imageThree?.contain;

    return (
        <Container className={className}>
            {title || description ? (
                <div className={clsx('mb-6 lg:mb-8')}>
                    <Typography
                        variant={titleSize === 'large' ? 'hd-3xl' : 'hd-lg'}
                        desktopVariant={titleSize === 'large' ? 'hd-4xl' : 'hd-lg'}
                        as="h2"
                    >
                        {titleSize === 'large' ? title : <strong>{title}</strong>}
                    </Typography>
                    {description ? (
                        <RichText
                            value={description}
                            className={clsx({
                                'mt-3 md:mt-4 lg:mt-6': !!title,
                            })}
                        />
                    ) : null}

                    {links && links.length > 0 ? (
                        <SanityLinkList links={links} size="md" className="mt-8" />
                    ) : null}
                </div>
            ) : null}

            <ul
                className={clsx('relative', {
                    'grid lg:grid-cols-3 lg:gap-4 xl:gap-8 ': imageLayout === '2',
                    'grid lg:grid-cols-4 lg:gap-4 xl:gap-8 ': imageLayout === '3',
                })}
            >
                <li
                    className={clsx({
                        'lg:col-span-2': imageLayout === '2',
                    })}
                >
                    <div className=" aspect-[4/3] relative flex items-center justify-center">
                        <SanityImage
                            image={imageOne}
                            className={clsx('rounded-2', {
                                'object-contain w-full h-fit max-h-full min-h-0': imageOneContain,
                                'object-cover w-full h-full': !imageOneContain,
                            })}
                        />
                    </div>
                    {showAllButton ? (
                        <Button
                            className={clsx('absolute bottom-4 left-4 md:bottom-8 md:left-8 z-10', {
                                'lg:hidden': imageLayout === '3',
                            })}
                            variant="primary-dark"
                            onClick={() => lightbox.open(0)}
                        >
                            {translate(language, 'viewAll')} ({imageCount})
                        </Button>
                    ) : null}
                </li>
                {imageTwo ? (
                    <li
                        className={clsx('hidden lg:block', {
                            'lg:col-span-2 lg:relative': imageLayout === '3',
                        })}
                    >
                        <div className=" aspect-[4/3] relative flex items-center justify-center">
                            <SanityImage
                                image={imageTwo}
                                className={clsx('rounded-2', {
                                    'object-contain w-full h-fit max-h-full min-h-0':
                                        imageTwoContain,
                                    'object-cover w-full h-full': !imageTwoContain,
                                })}
                            />
                        </div>
                        <Button
                            className={clsx('absolute bottom-4 left-4 md:bottom-8 md:left-8 z-10')}
                            variant="primary-dark"
                            onClick={() => lightbox.open(0)}
                        >
                            {translate(language, 'viewAll')} ({imageCount})
                        </Button>
                    </li>
                ) : null}

                {imageThree ? (
                    <li
                        className={clsx('hidden lg:block', {
                            'lg:self-end': imageLayout === '3',
                        })}
                    >
                        <div className=" aspect-[4/3] relative flex items-center justify-center">
                            <SanityImage
                                image={imageThree}
                                className={clsx('rounded-2', {
                                    'object-contain w-full h-fit max-h-full min-h-0':
                                        imageThreeContain,
                                    'object-cover w-full h-full': !imageThreeContain,
                                })}
                            />
                        </div>
                    </li>
                ) : null}
            </ul>
        </Container>
    );
}
